import * as React from 'react';
import {
  CheckmarkFillIcon,
  NumberFilled1Icon,
  NumberFilled2Icon,
  NumberFilled3Icon,
  NumberFilled4Icon,
  NumberFilled5Icon,
  NumberFilled6Icon,
  NumberFilled7Icon,
  NumberFilled8Icon,
  NumberFilled9Icon,
  NumberOutline1Icon,
  NumberOutline2Icon,
  NumberOutline3Icon,
  NumberOutline4Icon,
  NumberOutline5Icon,
  NumberOutline6Icon,
  NumberOutline7Icon,
  NumberOutline8Icon,
  NumberOutline9Icon,
} from '@align-cpf/uikit-icons';
import { IIconProps, Icon } from '../../../atoms';
import { Typography } from '../../Typography';
import { clsx } from 'clsx';
import { createLocators } from '@u1/locators';
import useStyles from './Step.styles';

export interface IStepProps {
  /**
   * Step title
   */
  title: string;
  /**
   * Step description
   */
  description?: string;
  /**
   * Index of the element during rendering. Managed by Stepper component
   */
  index: number;
  /**
   * Highlights active step
   */
  highlighted?: boolean;
  /**
   * Highlights completed step
   */
  completed?: boolean;
  /**
   * Step alignment
   */
  variant: 'horizontal' | 'vertical';
}

export const stepLocators = createLocators({
  name: 'Step',
  componentLocators: ['container', 'title', 'description', 'icon'],
});

export const indexToNumberOutlinedMap: Record<number, IIconProps['icon']> = {
  0: NumberOutline1Icon,
  1: NumberOutline2Icon,
  2: NumberOutline3Icon,
  3: NumberOutline4Icon,
  4: NumberOutline5Icon,
  5: NumberOutline6Icon,
  6: NumberOutline7Icon,
  7: NumberOutline8Icon,
  8: NumberOutline9Icon,
};

export const indexToNumberFilledMap: Record<number, IIconProps['icon']> = {
  0: NumberFilled1Icon,
  1: NumberFilled2Icon,
  2: NumberFilled3Icon,
  3: NumberFilled4Icon,
  4: NumberFilled5Icon,
  5: NumberFilled6Icon,
  6: NumberFilled7Icon,
  7: NumberFilled8Icon,
  8: NumberFilled9Icon,
};

const Step: React.FC<IStepProps> = ({
  title,
  description,
  completed,
  highlighted,
  index,
  variant = 'horizontal',
}) => {
  const classes = useStyles();
  const { locators } = stepLocators();

  const stepIcon = completed
    ? CheckmarkFillIcon
    : highlighted
    ? indexToNumberFilledMap[index]
    : indexToNumberOutlinedMap[index];

  return (
    <div
      {...locators.container({ highlighted: highlighted || completed })}
      className={clsx(classes.step, classes[variant], {
        [classes.highlighted]: highlighted || completed,
      })}
    >
      {stepIcon ? (
        <Icon
          size={18}
          icon={stepIcon}
          className={classes.icon}
          locatorsOverrides={locators.icon()}
        />
      ) : null}

      <div className={classes.text}>
        <Typography
          as="div"
          variant="body01"
          hideOverflow
          locatorsOverrides={locators.title()}
        >
          {title}
        </Typography>
        <Typography
          as="div"
          variant="label01"
          hideOverflow
          className={classes.description}
          locatorsOverrides={locators.description()}
        >
          {description}
        </Typography>
      </div>
    </div>
  );
};

export default Step;
